import React, { useEffect, useState, useRef } from 'react';
import './main.css';
import Onglets from './Onglets.js';
import CharPie from './CharPie';
import PopChart from './PopChart';
import Indicateurs from './Indicateurs';
import './charts.css';

function Main({ indicateurInfo, valeurs }) {
  const hasValues = valeurs && valeurs.length > 0;
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const indicatorsRef = useRef(null);
  const headerRef = useRef(null);
  const spacerRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Détecter les changements de taille d'écran
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Effet pour marquer le chargement quand indicateurInfo change
  useEffect(() => {
    // Si on a des infos sur l'indicateur, marquer comme chargé
    if (indicateurInfo) {
      setLoaded(true);
    } else {
      setLoaded(false);
    }
  }, [indicateurInfo]);

  // Gestion du comportement sticky de la bannière de titre et des indicateurs
  useEffect(() => {
    // Créer un placeholder pour l'espace
    const indicatorPlaceholder = document.createElement('div');
    indicatorPlaceholder.className = 'indicator-placeholder';
    indicatorPlaceholder.style.display = 'none';
    document.querySelector('.content-inner')?.appendChild(indicatorPlaceholder);

    const handleScroll = () => {
      // Gestion du header fixe
      if (headerRef.current) {
        if (window.scrollY > 10) {
          setIsHeaderSticky(true);
          headerRef.current.classList.add('sticky');
          
          // Ajouter un espace pour compenser la hauteur du header fixé
          if (spacerRef.current) {
            spacerRef.current.classList.add('active');
            spacerRef.current.style.height = `${headerRef.current.offsetHeight}px`;
          }
        } else {
          setIsHeaderSticky(false);
          headerRef.current.classList.remove('sticky');
          
          // Supprimer l'espace compensatoire
          if (spacerRef.current) {
            spacerRef.current.classList.remove('active');
          }
        }
      }
      
      // Gestion des indicateurs fixes
      if (indicatorsRef.current) {
        const headerHeight = headerRef.current?.offsetHeight || 80;
        const triggerPoint = isHeaderSticky ? 20 : headerHeight + 20;
        
        if (window.scrollY > triggerPoint) {
          if (!isSticky) {
            setIsSticky(true);
            indicatorsRef.current.classList.add('is-sticky');
            
            // Mise à jour du placeholder avant de devenir sticky
            const indicatorHeight = indicatorsRef.current.offsetHeight;
            indicatorPlaceholder.style.height = `${indicatorHeight}px`;
            indicatorPlaceholder.style.display = 'block';
            indicatorPlaceholder.style.marginBottom = '20px';
            
            // Insérer le placeholder juste après la div des indicateurs
            if (indicatorsRef.current.nextSibling) {
              indicatorsRef.current.parentNode.insertBefore(indicatorPlaceholder, indicatorsRef.current.nextSibling);
            }
          }
        } else {
          if (isSticky) {
            setIsSticky(false);
            indicatorsRef.current.classList.remove('is-sticky');
            
            // Cacher le placeholder quand les indicateurs ne sont plus sticky
            indicatorPlaceholder.style.display = 'none';
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Nettoyer le placeholder à la fin
      document.querySelector('.indicator-placeholder')?.remove();
    };
  }, [isHeaderSticky, isSticky]);

  // Ajout d'un effet pour corriger l'espacement
  useEffect(() => {
    if (indicatorsRef.current && isSticky) {
      // S'assurer que le placeholder a la bonne taille quand isSticky change
      const placeholder = document.querySelector('.indicator-placeholder');
      if (placeholder) {
        placeholder.style.height = `${indicatorsRef.current.offsetHeight}px`;
        placeholder.style.display = 'block';
      }
    }
  }, [isSticky]);

  // Calculer les dimensions du fond en fonction de la taille de l'écran
  const getSidebarWidth = () => {
    if (windowWidth < 768) {
      return 0; // Sur mobile, la sidebar est cachée ou s'affiche en overlay
    } else if (windowWidth < 1200) {
      return 0; // Sur tablette en mode replié
    } else {
      return 320; // Desktop
    }
  };

  // Style CSS pour l'image de fond en plein écran
  const backgroundStyle = {
    position: 'fixed',
    top: '60px',
    left: getSidebarWidth() + 'px',
    width: `calc(100% - ${getSidebarWidth()}px)`,
    height: 'calc(100vh - 60px)',
    transition: 'all 0.3s ease',
    zIndex: loaded ? -1 : 900,
    opacity: loaded ? 0 : 1,
    backgroundImage: 'url("./images/font.png")',
    backgroundSize: '100% 100%',
    backgroundPosition: 'center',
    visibility: loaded ? 'hidden' : 'visible'
  };

  // Style pour le contenu quand un indicateur est sélectionné
  const contentStyle = {
    padding: windowWidth < 768 ? '10px' : '0',
    width: '100%',
    overflowX: 'hidden',
    maxWidth: windowWidth > 1600 ? '1600px' : '100%',
    margin: windowWidth > 1600 ? '0 auto' : '0',
    position: 'relative',
    zIndex: loaded ? 1000 : 0
  };
  
  // Détecter si la sidebar est visible ou non
  const isSidebarToggled = document.body.classList.contains('toggle-sidebar');

  // Vérifier si l'indicateur actuel est "Taux d'épargne nationale"
  const isSavingsRate = indicateurInfo && indicateurInfo.nom === "Taux d'épargne nationale";

  // Style pour les indicateurs fixes
  const indicatorsStyle = isSticky ? {
    position: 'fixed',
    top: isHeaderSticky ? `${headerRef.current?.offsetHeight || 80}px` : '80px',
    left: 0,
    right: 0,
    zIndex: 1040,
    backgroundColor: 'white',
    boxShadow: '0 3px 10px rgba(0, 0, 0, 0.05)',
    padding: '15px 20px',
    width: '100%'
  } : {};

  return (
    <main id="main" className="main" style={{padding: 0}}>
      <div className="background-container" style={backgroundStyle}></div>
      
      {indicateurInfo && (
        <div style={contentStyle} className={`main-content ${isSidebarToggled ? 'full-width' : ''}`}>
          {/* Header avec le titre principal stylisé comme dans l'image */}
          <div 
            ref={headerRef}
            className={`header-banner ${isSidebarToggled ? 'full-width' : ''}`} 
            id="pageTitle"
          >
            <div className="header-content">
              <div className="vertical-line"></div>
              <div className="header-text">
                <h1>SUIVI DES INDICATEURS INTERNATIONAUX</h1>
                <div className="subtitle-container">
                  <h2>{indicateurInfo.nom || 'PIB par habitant'}</h2>
                  <span className="orange-dot"></span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Espace pour compenser la hauteur du header quand il est fixe */}
          <div ref={spacerRef} className="header-spacer"></div>
          
          <div className="content-inner">
            {hasValues ? (
              <div className={`dashboard-container ${isSidebarToggled ? 'sidebar-hidden' : ''}`}>
                {/* Section des indicateurs (2 cartes côte à côte) */}
                <div 
                  ref={indicatorsRef}
                  className={`indicators-row mb-4 ${isSavingsRate ? 'sticky-indicator' : ''}`} 
                  style={isSavingsRate ? indicatorsStyle : {}}
                >
                  <div className="indicator-col international-value">
                    <Indicateurs
                      title="Valeur Internationale"
                      value={valeurs[0].valeur}
                      icon="fa-globe"
                      iconcolor="var(--primary-green)"
                      isSticky={isSavingsRate && isSticky}
                      trend="up"
                    />
                  </div>
                  <div className="indicator-col national-value">
                    <Indicateurs
                      title="Valeur Nationale"
                      value={valeurs[0].valeur}
                      icon="fa-flag"
                      iconcolor="var(--primary-orange)"
                      isSticky={isSavingsRate && isSticky}
                      trend="down"
                    />
                  </div>
                </div>

                {/* Section du graphique de répartition régionale */}
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <CharPie
                      series={valeurs.slice(0, 4).map(v => v.valeur)}
                      options={{
                        labels: ['Amérique du Nord', 'Europe', 'Asie-Pacifique', 'Afrique'],
                        colors: ['var(--primary-green)', 'var(--muted-orange)', 'var(--secondary-green)', 'var(--light-green)'],
                        chart: {
                          type: 'pie',
                          fontFamily: 'Montserrat, sans-serif',
                          animations: {
                            enabled: true,
                            speed: 500
                          }
                        },
                        plotOptions: {
                          pie: {
                            customScale: 1,
                            offsetY: 0,
                            dataLabels: {
                              offset: 0
                            }
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: function(val, opts) {
                            return val.toFixed(1) + '%';
                          },
                          style: {
                            fontSize: '18px',
                            fontWeight: '700',
                            textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                          },
                          background: {
                            opacity: 0.7
                          }
                        }
                      }}
                      title="Répartition Régionale"
                    />
                  </div>
                </div>

                {/* Section du graphique d'évolution sur 10 ans */}
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <PopChart 
                      series={[
                        { name: 'Valeur Nationale', data: valeurs.map(v => v.valeur) }
                      ]} 
                      colors={['var(--primary-green)']}
                      height={windowWidth < 768 ? 350 : 400}
                      title="Évolution sur 10 ans"
                    />
                  </div>
                </div>

                {/* Section des onglets d'information détaillée */}
                <div className="row g-0 mb-4">
                  <div className="col-12">
                    <Onglets indicateurInfo={indicateurInfo} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="alert alert-info" style={{margin: '50px 20px', padding: '30px', textAlign: 'center', fontFamily: 'Montserrat, sans-serif', color: '#506559', backgroundColor: 'rgba(80, 101, 89, 0.1)', border: 'none', borderRadius: '8px'}}>
                <i className="fas fa-info-circle me-2"></i>
                Pas de données disponibles pour le moment.
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

export default Main;



